/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { parseBackendErrors } from "../helpers/parseBackendErrors";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Button,
  TextField,
  Dialog,
  InputAdornment,
  MenuItem,
  Alert,
  Link as MuiLink,
  Tab,
  Tabs,
  Box,
  Typography
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { ToggleDepositModal } from "./../store/actions/app";
import Selector from "./Selector";
import { Link } from "react-router-dom";
import { addBalance } from "./../helpers/banking";
import { requestWithdrawal } from "./../helpers/withdraw";
import { SetBalance } from "./../store/actions/balance";
import { getBalances } from "./../helpers/balance";
import classNames from "classnames";
import styles from "./../sass/purchase.module.scss";

const Deposit = () => {

  // state
  const [submitting, setSubmitting] = useState(false);
  const [is_error_visible, setErrorVisibile] = useState(false);
  const [is_success, setSuccess] = useState(false);
  const [error, setError] = useState("Something went wrong");
  const [tab, setTab] = useState(0);
  const [asset, setAsset] = useState(null);
  const [withdrawalFee, setWithdrawalFee] = useState(0);

  // redux
  const { deposit_opened, balance_tab, user, token } = useSelector((state) => state.app);
  const { list } = useSelector((state) => state.balance);
  const dispatch = useDispatch();

  // hooks
  const { control, handleSubmit, errors, setValue, watch } = useForm();
  const { enqueueSnackbar } = useSnackbar();
  const amount = watch("amount");
  
  // lifecycles
  useEffect(() => {
    if(tab === 1) {
      const findAsset = list.find(item => {
        return item.asset.symbol === "tether"
      });
      if(findAsset) {
        setAsset(findAsset);
      }
    }
  },[tab]);

  useEffect(() => {
    if(tab === 1 && amount && asset) {
      const { asset: {  withdrawal_fee_percent } } = asset;
      const fee = (parseFloat(amount) * withdrawal_fee_percent/100);
      setWithdrawalFee(fee);
    }
  },[amount,tab,asset])

  useEffect(() => {
    if(!deposit_opened) {
      setSuccess(false);
      setErrorVisibile(false);
      setError("");
      setWithdrawalFee(0);
    } else {
      setTab(balance_tab);
    }
  },[deposit_opened])

  // methods
  const handleFormSubmission = async (form) => {
    setSubmitting(true);
    setSuccess(false);
    setErrorVisibile(false);
    setError("");
    if(tab === 0) {
      const [error,] = await addBalance(
        token,
        {
          account: form.account,
          amount: parseFloat(form.amount)
        }
      );
      if(error) {
        const parsed_errors = parseBackendErrors(error.response);
        setError(parsed_errors);
        setErrorVisibile(true);
        setSubmitting(false);
        return;
      }
      enqueueSnackbar("Success! Your deposit is now being processed.", {
        variant: "success",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });
      setSuccess(true);
      setSubmitting(false);
      setValue("amount", "");
    } else {
      const [error,] = await requestWithdrawal(
        token,
        {
          amount: parseFloat(form.amount),
          account: form.account,
        }
      );
      if(error) {
        const parsed_errors = parseBackendErrors(error.response);
        setError(parsed_errors);
        setErrorVisibile(true);
        setSubmitting(false);
        return;
      }
      enqueueSnackbar("Success! Your withdrawal request is now being processed.", {
        variant: "success",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });
      setSuccess(true);
      setSubmitting(false);
      setValue("amount", "");
      handleGetBalances(token);
    }
  };

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
  }

  const handleGetBalances = async (token) => {
    const [err, response] = await getBalances(token);
    if (err) {
      dispatch(SetBalance([]));
      return;
    }
    dispatch(SetBalance(response.data));
  };

  return (
    <Dialog
      open={deposit_opened}
      fullWidth={true}
      onClose={() => {
        dispatch(ToggleDepositModal(false));
      }}
    >
      <Box mx={3} mt={1}>
        <Tabs 
          value={tab} 
          onChange={handleTabChange}
          variant="fullWidth"
          className="my-3 fancyTabs"
          >
          <Tab label="Deposit USD"/>
          <Tab label="Withdraw USD"/>
        </Tabs>
      </Box>
      {
        (user && (!user.bank_accounts || (user.bank_accounts && user.bank_accounts.length === 0))) && (
          <Alert
            severity="error"
            className="mx-4 mb-3"
          >
            You don't have any bank accounts. <MuiLink 
              component={Link} to="/dashboard/bank/new"
              onClick={() => dispatch(ToggleDepositModal(false))}
              >Add New</MuiLink> to continue.
          </Alert>
        )
      }

      <form onSubmit={handleSubmit(handleFormSubmission)} className="px-4 pb-4">
        <Controller
          as={
            <TextField
              label="Amount *"
              size="small"
              autoComplete="new-password"
              fullWidth={true}
              variant="outlined"
              error={errors.amount && true}
              helperText={errors?.amount?.message}
              className="mb-3"
              inputProps={{ maxLength: 8 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {tab === 0 ? "USD" : 
                      <>/{" "} 
                        {
                          asset?.amount?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })
                        } USD
                      </>
                    }
                  </InputAdornment>
                ),
              }}
            />
          }
          name="amount"
          control={control}
          defaultValue={""}
          rules={{
            required: "This field is required.",
            pattern: {
              value: /^\d+(\.\d+)?$/i,
              message: "Please enter a valid amount (eg. 125.38).",
            },
            validate: (value) => {
              if (parseFloat(value) < 500) {
                return `Minimum ${tab === 0 ? "deposit amount" : "amount to withdraw"} is $500 USD.`;
              } else if(parseFloat(value) > 50000) {
                return `Maximum ${tab === 0 ? "deposit amount" : "amount to withdraw"} is $50000 USD.`;
              }
            },
          }}
        />
        <Selector
          id="type"
          name="account"
          className={"mb-3"}
          label="Account *"
          control={control}
          defaultValue={""}
          variant="outlined"
          fullWidth={true}
          rules={{ 
            required: "This field is required." 
          }}
          size="small"
          errors={errors}
          disabled={(user && (!user.bank_accounts || (user.bank_accounts && user.bank_accounts.length === 0)))}
        >
          {
            user && user.bank_accounts.map(item => {
              return (
                <MenuItem key={item._id} value={item._id}>{item.bank} {item.last4}</MenuItem>
              )
            })
          }
        </Selector>

        {
          (tab === 1 && asset) && (
            <div
              className={classNames(
                styles["purchase-preview"],
                ""
              )}
            >
              <Typography variant="h4">Preview</Typography>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <Typography variant="body2">Commission</Typography>
                <Typography variant="body2">
                  
                    {
                      withdrawalFee < asset?.asset?.withdrawal_fee_amount ? (
                        <><strong>{asset?.asset?.withdrawal_fee_percent}%</strong> / {withdrawalFee.toLocaleString("en-US", {style: "currency", currency: "USD"})}</>
                      ) : (
                        <strong>{asset?.asset?.withdrawal_fee_amount.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}</strong>
                      )
                    }

                </Typography>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <Typography variant="body2">Total</Typography>
                <Typography variant="body2">
                  <strong>
                    {
                      withdrawalFee < asset?.asset?.withdrawal_fee_amount ? (
                        (parseFloat(amount ? amount : 0) - withdrawalFee).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })
                      ) : (
                        (parseFloat(amount ? amount : 0) - asset?.asset?.withdrawal_fee_amount).toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })
                      )
                    }
                  </strong>
                </Typography>
              </div>
            </div>
          )
        }

        {is_success && (
          <Alert className="mb-3" severity="success">
            Your {tab === 0 ? "deposit" : "withdrawal request"} is being processed. You can check its status <MuiLink 
              component={Link} 
              to={tab === 0 ? "/dashboard/deposits" : "/dashboard/withdrawals"}
              onClick={() => dispatch(ToggleDepositModal(false))}
              >here</MuiLink>.
          </Alert>
        )}

        {is_error_visible ? (
          <Alert className="mb-3" severity="error">
            {error}
          </Alert>
        ) : null}
        
        <div className="d-flex justify-content-between">
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => {
              dispatch(ToggleDepositModal(false));
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={submitting}
            variant="contained"
            color="primary"
            type="submit"
          >
            {
              tab === 0 ? "Deposit" : "Withdraw"
            }
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
export default Deposit;
